'use client';

import { useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/auth-context';
import { Button } from '@/components/ui/button';
import { LogOut, ArrowLeft } from 'lucide-react';
import { Logo } from '@/components/logo';

/**
 * Simple header component for admin dashboard.
 *
 * Provides minimal navigation with:
 * - Logo linking to admin dashboard
 * - Return to user dashboard link
 * - User email display
 * - Logout button
 *
 * @returns Admin header with minimal navigation
 *
 * @example
 * ```tsx
 * <AdminHeader />
 * ```
 *
 * @see {@link ./admin-layout-client.tsx} - Admin layout that uses this header
 * @see {@link ./header.tsx} - Full dashboard header for comparison
 */
export function AdminHeader() {
  const { user, logout } = useAuth();
  const router = useRouter();

  const handleLogout = useCallback(() => {
    logout();
    router.push('/');
  }, [logout, router]);

  return (
    <header className="relative sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-14 items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-4">
          <Logo href="/admin" size="sm" />
          <Link
            href="/dashboard"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Return to Dashboard</span>
          </Link>
        </div>

        <div className="flex items-center gap-4">
          {user && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{user.email}</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="flex items-center gap-2"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </div>
    </header>
  );
}
